// client/src/hooks/useEmbedAutoResize.js
//
// Inside an iframe embed (/embed/...), post the embed's content height to the
// parent page whenever it changes. The parent's /embed/resize.js (served by
// the server, see services/embedEvents.js) applies it to the iframe that sent
// it. Same message as the server-rendered events embed:
//   { type: 'onscreen:embed:height', height }
//
// Pass a ref to the embed's root element. documentElement.scrollHeight (and
// body / #root, which carry min-height: 100vh) never report less than the
// iframe's own height, so measuring them lets the frame grow but never shrink.
// Growth Phase 8. No-op when not embedded or not in a frame.

import { useEffect } from 'react';

export const EMBED_HEIGHT_MESSAGE = 'onscreen:embed:height';

export default function useEmbedAutoResize(enabled, ref) {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.parent === window) return undefined;
    const el = (ref && ref.current) || document.body;
    let last = -1; // always send the first measurement
    const send = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h === last) return;
      last = h;
      try { window.parent.postMessage({ type: EMBED_HEIGHT_MESSAGE, height: h }, '*'); } catch { /* ignore */ }
    };
    send();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(send);
      ro.observe(el);
    }
    window.addEventListener('load', send);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('load', send);
    };
  }, [enabled, ref]);
}
